import {
  PageItem_DoubleTileContentBlock,
  PageItem_DoubleTileContentBlock_Side,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/story_page/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {TrackingPromotionCreative} from '@aboutyou/legacy-tracking-sdk';
import {TrackPromotions} from '@aboutyou/tracking';
import {
  ContentBlockImageTile,
  ContentBlockVideoAutoPlay,
  GRID_GUTTER_WIDTH,
  ImageRatio,
  RADIUS,
  SPACING,
  UnstyledLink,
  gridContainer,
  mediaTabletAndUp,
  mobileOnlyLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {StoryTileOverlay} from './StoryTileOverlay';

export const StoryDoubleTile: React.FC<{
  content: PageItem_DoubleTileContentBlock;
  streamPosition: number;
  testId?: string;
}> = ({content, streamPosition, testId = 'storyDoubleTile'}) => {
  const {ratio, left, right} = content;

  if (ratio === undefined || left === undefined || right === undefined) {
    return null;
  }

  const aspectRatio = `${ratio.width}:${ratio.height}` as ImageRatio;

  return (
    <Wrapper data-testid={testId}>
      <Column
        data-testid={`${testId}LeftTracker`}
        promotion={{
          source: TrackingPromotionCreative.GenericPromotion,
          promotionTracker: left.promotionTracker,
        }}
        streamPosition={streamPosition}
        horizontalPosition={0}
      >
        <Side side={left} aspectRatio={aspectRatio} testId={`${testId}Left`} />
      </Column>
      <Column
        data-testid={`${testId}RightTracker`}
        promotion={{
          source: TrackingPromotionCreative.GenericPromotion,
          promotionTracker: right.promotionTracker,
        }}
        streamPosition={streamPosition}
        horizontalPosition={1}
      >
        <Side
          side={right}
          aspectRatio={aspectRatio}
          testId={`${testId}Right`}
        />
      </Column>
    </Wrapper>
  );
};

const Side: React.FC<{
  side: PageItem_DoubleTileContentBlock_Side;
  aspectRatio: ImageRatio;
  testId: string;
}> = ({side, aspectRatio, testId}) => {
  const {t} = useTranslation();
  const {media, headline, subline, cta} = side;
  const ctaUrl = cta?.linkTarget?.url;
  // show=false + a link makes the whole tile tappable; show=true renders a button.
  const tileHref = cta && !cta.show && ctaUrl ? ctaUrl : undefined;
  const ctaButton =
    cta?.show && ctaUrl
      ? {text: cta.text || t('Discover more'), url: ctaUrl}
      : undefined;

  switch (media?.$case) {
    case 'image':
      return (
        <Frame>
          <ContentBlockImageTile
            testId={testId}
            href={tileHref}
            image={{
              url: media.image.url,
              description: media.image.description,
              aspectRatio,
            }}
          />
          <StoryTileOverlay
            headline={headline}
            subline={subline}
            cta={ctaButton}
            testId={testId}
          />
        </Frame>
      );

    case 'video': {
      const {video} = media;

      const tile = (
        <Frame>
          <ContentBlockVideoAutoPlay
            testId={testId}
            video={{
              sources: video.sources,
              lowSources: video.lowSources,
              description: video.description,
              aspectRatio,
              fallbackImage: {
                url: video.fallbackImage.url,
                description: video.fallbackImage.description,
                aspectRatio,
              },
            }}
            disablePlayPauseControl
            disableMuteUnmuteControl
          />
          <StoryTileOverlay
            headline={headline}
            subline={subline}
            cta={ctaButton}
            testId={testId}
          />
        </Frame>
      );

      // Unlike the image tile, the video has no href prop, so it is the whole
      // frame that becomes the link.
      return tileHref ? (
        <UnstyledLink
          to={tileHref}
          aria-label={video.description}
          data-testid={`${testId}Link`}
        >
          {tile}
        </UnstyledLink>
      ) : (
        tile
      );
    }

    default:
      return null;
  }
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${mobileOnlyLinaria(`gap: ${SPACING['Y.Lg']};`)}

  @media ${mediaTabletAndUp} {
    ${gridContainer}
    flex-direction: row;
  }
`;

const Column = styled(TrackPromotions)`
  display: flex;
  min-width: 0;

  @media ${mediaTabletAndUp} {
    width: calc((100% - ${GRID_GUTTER_WIDTH}) / 2);
  }
`;

const Frame = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: ${RADIUS.Sm};
`;
